import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CoverageAreaDetailsForm } from './CoverageAreaDetailsForm';
import { Plus } from 'lucide-react';
import { useToast } from 'src/components/useToast';
import { trpc } from 'src/trpc';

interface NewCoverageAreaModalProps {
  organizationId: string;
  onSuccess: () => void;
}

export const NewCoverageAreaModal = ({ organizationId, onSuccess }: NewCoverageAreaModalProps) => {
  const [open, setOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  const handleSuccess = () => {
    successToast({
      title: 'Coverage Area Created',
      description: 'New coverage area has been successfully created',
    });
    setOpen(false);
    utils.admin.organization.getOrganizationCoverageAreas.invalidate();
    utils.admin.organization.getUnassignedCoverageAreas.invalidate();
    onSuccess();
  };

  const handleError = (error: Error) => {
    errorToast({
      title: 'Failed to create coverage area',
      description: error.message || 'An error occurred while creating the coverage area',
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className=" bg-primary hover:bg-primary/90 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Add Coverage Area
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Coverage Area</DialogTitle>
          <DialogDescription>Add a new coverage area for this organization.</DialogDescription>
        </DialogHeader>
        <CoverageAreaDetailsForm
          organizationId={organizationId}
          onSuccess={handleSuccess}
          onError={handleError}
          onStateChange={(state) => setIsFormValid(state.isValid)}
        >
          <div className="flex flex-row justify-end gap-4 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!isFormValid} type="submit">
              Create Coverage Area
            </Button>
          </div>
        </CoverageAreaDetailsForm>
      </DialogContent>
    </Dialog>
  );
};
